import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import { createSelectors } from '@/Lib';
import { ApiV2, Code } from '@/Services/api';
import { PaginationType } from '@/Services/api/methods';
import { DictionaryKeysEnum } from '@/Services/api/methods/v2/Dictionary';
import { CommonDictionaryFilter } from '@/Services/api/methods/v2/Dictionary/request';
import { DictionaryResponse, DictionaryResponseKeys } from '@/Services/api/methods/v2/Dictionary/response';
import { NotificationsQuery, ReadNotificationsRequest, StationsQuery } from '@/Services/api/methods/v2/Notifications/request';
import { NotificationResource, StationMonitoringResource } from '@/Services/api/methods/v2/Notifications/response';

export type NotificationsState = {
    filteredDictionaries: DictionaryResponseKeys | null;
    selectedFilters: CommonDictionaryFilter;
    stations: StationMonitoringResource[];
    notifications: NotificationResource[];
};

export type NotificationsActions = {
    loadFilteredDictionaries: () => void;
    updateFilters: (filters: Partial<CommonDictionaryFilter>) => void;
    setFilteredDictionaries: (dictionaries: DictionaryResponse) => void;
    setSelectedFilters: (filters: CommonDictionaryFilter) => void;
    loadStations: (query?: StationsQuery, callback?: (pagination: PaginationType) => void) => void;
    loadNotifications: (query?: NotificationsQuery, callback?: (pagination: PaginationType) => void) => void;
    readNotifications: (request: ReadNotificationsRequest) => void;
};

export type NotificationsStore = NotificationsState & NotificationsActions;

const initialState: NotificationsState = {
    filteredDictionaries: null,
    selectedFilters: {
        companies_ids: [],
        stores_ids: [],
        store_groups_ids: [],
    },
    stations: [],
    notifications: [],
};

export const notificationsState = create<NotificationsStore>()(
    immer<NotificationsStore>((set, getState) => ({
        ...initialState,

        loadFilteredDictionaries: async () => {
            const { selectedFilters } = getState();

            const requestBodyFiltersObject = {
                companies: undefined,
                store_groups: {
                    companies_ids: selectedFilters.companies_ids,
                },
                stores: {
                    store_groups_ids: selectedFilters.store_groups_ids,
                    companies_ids: selectedFilters.companies_ids,
                },
            };

            const response = await ApiV2.dictionaries().dictionaries({
                keys: [DictionaryKeysEnum.COMPANIES, DictionaryKeysEnum.STORES, DictionaryKeysEnum.STORE_GROUPS],
                filters: requestBodyFiltersObject,
            });

            if (response.code !== Code.Success) {
                return;
            }

            set((state) => {
                state.filteredDictionaries = response.data.dictionaries;
            });
        },

        updateFilters: (newFilters) => {
            set((state) => {
                state.selectedFilters = {
                    ...state.selectedFilters,
                    ...newFilters,
                };
            });
        },

        setFilteredDictionaries: (dictionaries) => {
            set((state) => {
                state.filteredDictionaries = dictionaries.dictionaries;
            });
        },

        setSelectedFilters: (filters) => {
            set((state) => {
                state.selectedFilters = filters;
            });
        },

        loadStations: async (query, callback) => {
            const response = await ApiV2.notifications().stations(query);

            if (response.code !== Code.Success) {
                return;
            }

            set((state) => {
                state.stations = response.data.stations.items;
            });

            if (callback) {
                callback(response.data.stations.pagination);
            }
        },

        loadNotifications: async (query, callback) => {
            const response = await ApiV2.notifications().getNotifications(query);

            if (response.code !== Code.Success) {
                return;
            }

            set((state) => {
                state.notifications = response.data.notifications.items;
            });

            if (callback) {
                callback(response.data.notifications.pagination);
            }
        },

        readNotifications: async (request) => {
            const response = await ApiV2.notifications().readNotifications(request);

            if (response.code !== Code.Success) {
                return;
            }

            set((state) => {
                if (request.notifications_ids.length === 0) {
                    state.notifications = state.notifications.map((notification) => {
                        notification.is_read = true;

                        return notification;
                    });

                    return;
                }

                state.notifications = state.notifications.map((notification) => {
                    if (request.notifications_ids.includes(notification.id)) {
                        notification.is_read = true;
                    }

                    return notification;
                });
            });
        },
    }))
);

export const notificationsStoreSelectors = createSelectors(notificationsState);
